import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import disbursementsAPI from '../../api/disbursementsAPI';

const DisbursementPrintPage = () => {
  const { id } = useParams();
  const [disbursement, setDisbursement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDisbursement = async () => {
      try {
        const res = await disbursementsAPI.getById(id);
        setDisbursement(res.data);
      } catch (err) {
        setError(err.message || 'Failed to load disbursement');
      } finally {
        setLoading(false);
      }
    };

    fetchDisbursement();
  }, [id]);

  useEffect(() => {
    if (!disbursement || loading) return undefined;

    const previousTitle = document.title;
    const date = new Date(disbursement.date || Date.now());
    const yyyymmdd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(
      date.getDate()
    ).padStart(2, '0')}`;
    const referenceNo = String(disbursement.referenceNo || 'NA').replace(/[^a-zA-Z0-9-_]/g, '_');

    document.title = `Disbursement-${referenceNo}-${yyyymmdd}.pdf`;
    
    // Ensure title is set before printing
    const printTimer = setTimeout(() => {
      window.print();
    }, 100);

    return () => {
      clearTimeout(printTimer);
      document.title = previousTitle;
    };
  }, [disbursement, loading]);

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  if (error || !disbursement) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const totalDebits = disbursement.lines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const payeeName =
    disbursement.payeeSnapshot?.name ||
    disbursement.vendorId?.name ||
    disbursement.customerId?.name ||
    'N/A';
  const payeeTin = disbursement.payeeSnapshot?.tin || disbursement.vendorId?.tin || '';
  const payeeAddress = [
    disbursement.payeeSnapshot?.address?.street || disbursement.vendorId?.address?.street,
    disbursement.payeeSnapshot?.address?.city || disbursement.vendorId?.address?.city,
    disbursement.payeeSnapshot?.address?.province || disbursement.vendorId?.address?.province
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="bg-white p-8 min-h-screen" style={{ fontSize: '12pt' }}>
      {/* Watermark for voided */}
      {disbursement.status === 'voided' && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '120px',
            fontWeight: 'bold',
            color: 'rgba(220, 38, 38, 0.1)',
            zIndex: -1,
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          VOIDED
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-gray-900 pb-4 mb-6">
          {disbursement.accountingClient?.name && (
            <p className="text-center  font-bold text-gray-900 mt-0 text-2xl">{disbursement.accountingClient.name}</p>
          )}
          
          {disbursement.accountingClient?.address && (
            <div className="text-center text-xs text-gray-700 mt-1">
              {[
                disbursement.accountingClient.address.building,
                disbursement.accountingClient.address.street,
                disbursement.accountingClient.address.barangay,
                disbursement.accountingClient.address.cityMunicipality,
                disbursement.accountingClient.address.province,
                disbursement.accountingClient.address.zipCode
              ].filter(Boolean).join(', ')}
            </div>
          )}
          
          {disbursement.accountingClient?.contactInfo && (
            <div className="text-center text-xs text-gray-600 mt-1">
              {[
                disbursement.accountingClient.contactInfo.phone,
                disbursement.accountingClient.contactInfo.mobile,
                disbursement.accountingClient.contactInfo.email
              ].filter(Boolean).join(' | ')}
            </div>
          )}
          
          <h1 className="text-xl font-bold text-center mt-3 mb-1">DISBURSEMENT VOUCHER</h1>
          <p className="text-center text-sm text-gray-600">DV #{disbursement.referenceNo}</p>
        </div>

        {/* Disbursement Info */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-xs text-gray-600 mb-1">DATE:</p>
            <p className="font-semibold">{formatDate(disbursement.date)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600 mb-1">STATUS:</p>
            <p className="font-semibold uppercase">{disbursement.status}</p>
          </div>
        </div>

        {/* Check/Bank Info */}
        {(disbursement.checkNo || disbursement.orNumber) && (
          <div className="grid grid-cols-2 gap-6 mb-6 border-b pb-4">
            {disbursement.checkNo && (
              <div>
                <p className="text-xs text-gray-600 mb-1">CHECK NO:</p>
                <p className="font-semibold">{disbursement.checkNo}</p>
              </div>
            )}
            {disbursement.orNumber && (
              <div>
                <p className="text-xs text-gray-600 mb-1">OR NUMBER:</p>
                <p className="font-semibold">{disbursement.orNumber}</p>
              </div>
            )}
          </div>
        )}

        {/* Payee Information */}
        <div className="mb-6 border border-gray-300 rounded p-4 bg-gray-50">
          <h3 className="font-bold text-sm mb-3">PAYEE INFORMATION</h3>

          <div className="mb-2">
            <p className="text-xs text-gray-600">Name:</p>
            <p className="font-semibold">{payeeName}</p>
          </div>

          {payeeTin && (
            <div className="mb-2">
              <p className="text-xs text-gray-600">TIN:</p>
              <p className="font-semibold">{payeeTin}</p>
            </div>
          )}

          {payeeAddress && (
            <div>
              <p className="text-xs text-gray-600">Address:</p>
              <p className="font-semibold text-sm">{payeeAddress}</p>
            </div>
          )}
        </div>

        {/* Particulars (Memo) */}
        <div className="mb-6 border border-gray-300 rounded p-4">
          <h3 className="font-bold text-sm mb-2">PARTICULARS (MEMO)</h3>
          <p className="text-sm">{disbursement.memo || 'N/A'}</p>
        </div>

        {/* Line Details Table */}
        <div className="mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left text-xs font-bold">Account</th>
                <th className="border border-gray-300 p-2 text-right text-xs font-bold">Debit</th>
                <th className="border border-gray-300 p-2 text-right text-xs font-bold">Credit</th>
                <th className="border border-gray-300 p-2 text-left text-xs font-bold">Description</th>
              </tr>
            </thead>
            <tbody>
              {disbursement.lines.map((line, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 p-2 text-xs">
                    {line.accountId?.accountNumber} - {line.accountId?.accountName}
                  </td>
                  <td className="border border-gray-300 p-2 text-right text-xs">
                    {line.debit > 0 ? formatCurrency(line.debit) : ''}
                  </td>
                  <td className="border border-gray-300 p-2 text-right text-xs">
                    {line.credit > 0 ? formatCurrency(line.credit) : ''}
                  </td>
                  <td className="border border-gray-300 p-2 text-xs">{line.description || ''}</td>
                </tr>
              ))}

              {/* Totals Row */}
              <tr className="bg-gray-200 font-bold">
                <td className="border border-gray-300 p-2 text-xs text-right">TOTAL:</td>
                <td className="border border-gray-300 p-2 text-right text-xs">
                  {formatCurrency(totalDebits)}
                </td>
                <td className="border border-gray-300 p-2 text-right text-xs">
                  {formatCurrency(
                    disbursement.lines.reduce((sum, line) => sum + (line.credit || 0), 0)
                  )}
                </td>
                <td className="border border-gray-300 p-2 text-xs"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="flex justify-end mb-10">
          <div className="w-64">
            <div className="border-b border-gray-300 mb-2 pb-2">
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Total Amount:</span>
                <span className="font-bold">{formatCurrency(totalDebits)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Area */}
        <div className="mt-12">
          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="border-t border-gray-400 h-16"></div>
              <p className="text-xs font-semibold text-center">Prepared By</p>
            </div>
            <div>
              <div className="border-t border-gray-400 h-16"></div>
              <p className="text-xs font-semibold text-center">Approved By</p>
            </div>
            <div>
              <div className="border-t border-gray-400 h-16"></div>
              <p className="text-xs font-semibold text-center">Received By</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-600 border-t pt-4">
          <p>Printed: {new Date().toLocaleString('en-PH')}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .no-print {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default DisbursementPrintPage;
